import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {BehaviorSubject, forkJoin, Observable} from "rxjs";
import { map } from "rxjs/operators";
import {
  ActionResult,
  ActionResultList, ApplicationFormSettings,
  EventItem,
  FindEventsRequest,
  Race,
  RaceSettings
} from "@model";
import { EnvironmentService } from "./environment.service";
import {RaceSettingsService} from "./race-settings.service";
import {ApplicationFormSettingsService} from "./application-form-settings.serivce";
import {LocalStorageService} from "./local-storage.service";

@Injectable({ providedIn: 'root' })
export class PublicEventsService {
  private static _favoriteEvents: BehaviorSubject<ActionResultList<EventItem[], number> | null> = new BehaviorSubject<ActionResultList<EventItem[], number> | null>(null);
  private static _upcomingRegisteredEvents: BehaviorSubject<ActionResultList<EventItem[], number> | null> = new BehaviorSubject<ActionResultList<EventItem[], number> | null>(null);
  private static _pastRegisteredEvents: BehaviorSubject<ActionResultList<EventItem[], number> | null> = new BehaviorSubject<ActionResultList<EventItem[], number> | null>(null);

  get favoriteEvents(): BehaviorSubject<ActionResultList<EventItem[], number> | null> {
    return PublicEventsService._favoriteEvents;
  }

  get upcomingRegisteredEvents(): BehaviorSubject<ActionResultList<EventItem[], number> | null> {
    return PublicEventsService._upcomingRegisteredEvents;
  }

  get pastRegisteredEvents(): BehaviorSubject<ActionResultList<EventItem[], number> | null> {
    return PublicEventsService._pastRegisteredEvents;
  }

  emptyPublicEventsServiceStreams() {
    PublicEventsService._favoriteEvents.next(null);
    PublicEventsService._upcomingRegisteredEvents.next(null);
    PublicEventsService._pastRegisteredEvents.next(null);
  }

  private apiUrl = `${this.environmentService.getApiUrl()}/api/public/events/landing`;

  constructor(private http: HttpClient, private environmentService: EnvironmentService, private localStorageService: LocalStorageService,
              private raceSettingsService:RaceSettingsService, private applicationFormSettingsService: ApplicationFormSettingsService) {
  }


  findNewEventsList(): Observable<EventItem[]> {
    return this.fetchEventsList("new");
  }

  findUpcomingEventsList(): Observable<EventItem[]> {
    return this.fetchEventsList("upcoming");
  }

  private fetchEventsList(type: string): Observable<EventItem[]> {
    return this.http.get<ActionResultList<EventItem[], number>>(`${this.apiUrl}/${type}`).pipe(
      map(value => value?.result ?? [])
    );
  }

  findEventCalendarList(filter: FindEventsRequest): Observable<EventItem[]> {
    return this.http.post<ActionResultList<EventItem[], number>>(
      `${this.environmentService.getApiUrl()}/api/public/events/calendar/filter`, filter
    ).pipe(
      map(value => value?.result ?? [])
    );
  }

  filterFinishedEventsList(filter: FindEventsRequest): Observable<EventItem[]> {
    return this.http.post<ActionResultList<EventItem[], number>>(
      `${this.environmentService.getApiUrl()}/api/public/events/finished/filter`, filter
    ).pipe(
      map(value => value?.result ?? [])
    );
  }

  findEventByIdInner(eventId: number): Observable<EventItem | null> {
    return this.http.get<ActionResult<EventItem>>(`${this.environmentService.getApiUrl()}/api/public/events/geteventbyid?eventId=${eventId}`).pipe(
      map(value => value?.result ?? null)
    );
  }

  findEventById(eventId: number): Observable<EventItem | null> {
    return forkJoin(
      {
        event:this.findEventByIdInner(eventId),
        raceSettingsList: this.raceSettingsService.fetchRaceSettingsByEventId(eventId, false),
        applicationFormSettingsList: this.applicationFormSettingsService.fetchApplicationFormSettingsByEventId(eventId, false)
      })
      .pipe(map((data:any)=>{
        return this.combineRaceData(data);
      }));
  }

  private combineRaceData(data:any): EventItem|null {
    if (data.event) {
      data.event.racesList?.forEach((race: Race) => {
        race.raceSettings = data.raceSettingsList.find((item: RaceSettings) => item.raceId === race.id);
        race.applicationFormSettings = data.applicationFormSettingsList.find((item: ApplicationFormSettings) => item.raceId === race.id);
      });
      return data.event;
    }
    return null;
  }

  removeEventFromFavorites(eventId: number): Observable<ActionResult<boolean>> {
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/events/favorite/remove", eventId);
  }

  addEventToFavorites(eventId: number): Observable<ActionResult<boolean>> {
    return this.http.post<ActionResult<boolean>>(this.environmentService.getApiUrl() + "/api/events/favorite/add", eventId);
  }


  initUserFavoritesEvent(): void{
    this.fetchUserFavoritesEvent().subscribe()
  }

  fetchUserFavoritesEvent(): Observable<EventItem[]> {
    return this.http.get<ActionResultList<EventItem[], number>>(`${this.environmentService.getApiUrl()}/api/events/favorite/events`).pipe(
      map(value => {
        const result = value?.result ?? [];
        this.favoriteEvents.next(result.length ? value : null);
        return result;
      })
    );
  }

  fetchUserPastRegisteredEvents(): Observable<EventItem[]> {
    return this.http.get<ActionResultList<EventItem[], number>>(`${this.environmentService.getApiUrl()}/api/events/registered/past`).pipe(
      map(value => {
        const result = value?.result ?? [];
        this.pastRegisteredEvents.next(result.length ? value : null);
        return result;
      })
    );
  }

  fetchUserUpcomingRegisteredEvents(): Observable<EventItem[]> {
    return this.http.get<ActionResultList<EventItem[], number>>(`${this.environmentService.getApiUrl()}/api/events/registered/upcoming`).pipe(
      map(value => {
        const result = value?.result ?? [];
        this.upcomingRegisteredEvents.next(result.length ? value : null);
        return result;
      })
    );
  }





}
